import type { DocSection } from '../types'

export const planningSection: DocSection = {
  id: 'plannings',
  title: 'Planning & heures',
  icon: 'grid',
  accent: '#7C3AED',
  summary: "Votre journée type, les rappels avant chaque changement, et — pour les responsables — la validation des heures.",
  minRole: 'agent',
  articles: [
    {
      id: 'mon-planning',
      title: 'Mon planning',
      summary: 'Votre journée type et les notifications avant chaque changement.',
      icon: 'grid',
      blocks: [
        { type: 'p', text: "La page **Mon planning** (barre latérale) affiche la journée type qui vous a été assignée : réunions, plages de travail, pausettes, pause déjeuner, formations, coaching… avec les horaires et les durées." },
        { type: 'list', items: [
          "**5 minutes avant chaque changement** (pause, reprise, réunion, formation…), vous recevez une notification — impossible de rater la pausette de 15h00.",
          "Si un responsable vous **assigne un planning** ou **modifie** celui que vous suivez, vous êtes notifié immédiatement.",
          "Le bandeau en haut de la feuille indique le **prochain changement** de la journée.",
          "Certains jours peuvent finir plus tôt (ex. **départ à 21h00 le vendredi**) — c'est indiqué sous le tableau.",
        ] },
        { type: 'callout', variant: 'info', title: 'Pas encore de planning ?', text: "Si la page indique « Aucun planning assigné », un responsable ne vous en a pas encore attribué. Vous serez notifié dès que ce sera fait." },
        { type: 'seealso', items: [
          { to: 'statuts', label: 'Statuts & disponibilité' },
          { to: 'demarrer/connexion', label: 'Démarrer sa journée' },
        ] },
      ],
    },
    {
      id: 'gerer',
      title: 'Créer et assigner des plannings',
      summary: "Gabarits de journée, blocs, vendredi écourté, assignation à n'importe quel rôle.",
      icon: 'edit',
      minRole: 'admin',
      blocks: [
        { type: 'p', text: "L'onglet **Gestion des plannings** (page Mon planning) permet de créer autant de plannings que nécessaire et de les assigner aux utilisateurs — agents, superviseurs et même d'autres administrateurs. Deux plannings sont fournis par défaut : **Département prospection & commercial** et **Département technique & administration**." },
        { type: 'callout', variant: 'warning', title: 'Accès accordé par le propriétaire', text: "Le **Propriétaire** gère toujours les plannings. Un **Administrateur** ne voit ces onglets que si le propriétaire lui a accordé la **Gestion des plannings** (page Utilisateurs → compte admin)." },
        { type: 'steps', items: [
          "Créez un planning : nom, puis les **blocs** de la journée (début, fin, type — travail, réunion, pausette, déjeuner, formation, coaching — et libellé affiché).",
          "Réglez si besoin le **départ du vendredi** (ex. 21h00 au lieu de 22h00) et les **avis importants** affichés sous la feuille.",
          "Dans « Qui suit quel planning », choisissez le planning de chaque utilisateur — il est **notifié** aussitôt.",
        ] },
        { type: 'callout', variant: 'tip', title: 'Modification en direct', text: "Enregistrer une modification notifie automatiquement tous les utilisateurs assignés (« Votre planning a été mis à jour »)." },
      ],
    },
    {
      id: 'validation',
      title: 'Valider les heures des agents',
      summary: 'Retards, temps par statut, PC down, corrections, majoration, fériés, totaux.',
      icon: 'check-circle',
      minRole: 'admin',
      blocks: [
        { type: 'p', text: "L'onglet **Validation des heures** compare, jour par jour, le **planifié** au **réel** de chaque utilisateur assigné — calculé depuis le journal de statuts (le même que la page Temps agents) : « Disponible 7h05 · En appel 1h20 · Indisponible 6m »." },
        { type: 'h', text: 'Ce que vous voyez pour chaque journée' },
        { type: 'list', items: [
          "**Prise de poste** et **retard** par rapport à l'heure prévue du planning.",
          "Le **détail par statut** (les retraits) : disponible, en appel, pauses, indisponible…",
          "Le **total travaillé** calculé par le système (statuts productifs).",
        ] },
        { type: 'h', text: 'Vos ajustements avant validation' },
        { type: 'kv', items: [
          { k: 'PC down', v: "Panne technique subie par l'agent : le temps saisi est **rendu** (ajouté au total)." },
          { k: 'Correction ±', v: "Retirer ou ajouter des heures/minutes (prolongation comprise), avec un motif en note." },
          { k: 'Décalage', v: "Noter des horaires réellement décalés (information, ne change pas le total)." },
          { k: 'OFF / Congé / Férié', v: "Qualifier la journée — compté à part dans les totaux." },
          { k: 'Majoration %', v: "Pourcentage appliqué au total de la journée (ex. +25 % un férié travaillé) : le total **majoré** est calculé à côté du brut." },
        ] },
        { type: 'callout', variant: 'info', title: 'La validation fige le calcul', text: "Valider **photographie** le calcul système du moment (total + détail par statut) : les totaux de paie ne bougent plus même si les journaux évoluent. « Remettre à valider » annule la validation." },
        { type: 'p', text: "L'onglet **Totaux (paie)** additionne les journées **validées** d'une période, par utilisateur : total brut en heures/minutes/secondes, total **majoré**, retard cumulé, PC down, jours OFF/congés/fériés. L'onglet **Jours fériés** définit les fériés du compte, signalés dans la revue journalière." },
        { type: 'seealso', items: [
          { to: 'supervision/temps', label: 'La page Temps agents' },
        ] },
      ],
    },
  ],
}
